import React, { useEffect, useState } from 'react';
import { useTheme } from '@material-ui/core';
// @ts-ignore
import { instance } from '@viz-js/viz';
import { useNavigate } from 'react-router-dom';
import './GraphvizDiagramCard.css';
import { NumberInput } from './NumberInput';
import {
  C4DiagramModel,
  C4DiagramDeploymentNode,
  C4DiagramEntity,
  C4DiagramDeploymentNodeEntity,
} from './types';

interface ColorSchema {
  background: string;
  border: string;
  fontcolor: string;
}

export interface GraphvizDiagramCardProps {
  model: C4DiagramModel;
  edit: boolean;
  openContextMenu?: (element: HTMLElement) => void;
}

export const GraphvizDiagramCard = (props: GraphvizDiagramCardProps) => {
  const theme = useTheme();
  const colors: Record<string, ColorSchema> = {
    Error: { background: '#d60909 ', border: '#630202', fontcolor: '#ffffff' },
    Context: {
      background: '#00806A ',
      border: '#012f07',
      fontcolor: '#ffffff',
    },
    Database: {
      background: '#c9d5f7',
      border: '#2e6295',
      fontcolor: '#6e6e6e',
    },
    Service: { background: '#1e51de', border: '#2e6295', fontcolor: '#ffffff' },
    Website: { background: '#1e51de', border: '#2e6295', fontcolor: '#ffffff' },
    Job: { background: '#7293ec', border: '#2e6295', fontcolor: '#ffffff' },
    Workflow: {
      background: '#7293ec',
      border: '#2e6295',
      fontcolor: '#ffffff',
    },
    Library: { background: '#e5ebfb', border: '#2e6295', fontcolor: '#6e6e6e' },
    Group: { background: '#08427b', border: '#073b6f', fontcolor: '#ffffff' },
    System: { background: '#999999', border: '#6b6b6b', fontcolor: '#ffffff' },
    Provider: {
      background: '#999999',
      border: '#6b6b6b',
      fontcolor: '#ffffff',
    },
    'External System': {
      background: '#bbbbbb',
      border: '#999999',
      fontcolor: '#ffffff',
    },
    'External Provider': {
      background: '#bbbbbb',
      border: '#999999',
      fontcolor: '#ffffff',
    },
  };
  const navigate = useNavigate();
  const [model, setModel] = useState(props.model);
  const [zoom, setZoom] = useState(100);
  const [edit, setEdit] = useState(props.edit);

  useEffect(() => {
    setModel(props.model);
  }, [props.model]);

  useEffect(() => {
    setEdit(props.edit);
  }, [props.edit]);

  function wrapText(text?: string) {
    const maxLength = 20;
    let result = '';
    let line = '';
    if (text) {
      for (const element of text) {
        if (line.length >= maxLength && element === ' ') {
          result += line.trim() + '<br/>';
          line = '';
        } else {
          line += element;
        }
      }
      if (line.length > 0) {
        result += line.trim();
      }
    }
    return result.replaceAll('"', '&quot;').replace('&', '&amp;');
  }

  function wrapTooltip(text?: string) {
    const maxLength = 80;
    let result = '';
    let line = '';
    if (text) {
      for (const element of text) {
        if (line.length >= maxLength && element === ' ') {
          result += line.trim() + '\n';
          line = '';
        } else {
          line += element;
        }
      }
      if (line.length > 0) {
        result += line.trim();
      }
    }
    return result.replaceAll('"', "'");
  }

  function getDeploymentNodeEntities(
    nodes?: C4DiagramDeploymentNode[],
  ): C4DiagramDeploymentNodeEntity[] {
    const entities: C4DiagramDeploymentNodeEntity[] = [];
    if (nodes) {
      for (const node of nodes) {
        node.entities?.forEach(it => entities.push(it));
        getDeploymentNodeEntities(node.deploymentNodes).forEach(it =>
          entities.push(it),
        );
      }
    }
    return entities;
  }

  function getDeploymentNodes(
    nodes?: C4DiagramDeploymentNode[],
  ): C4DiagramDeploymentNode[] {
    const deploymentNodes: C4DiagramDeploymentNode[] = [];
    if (nodes) {
      for (const node of nodes) {
        deploymentNodes.push(node);
        getDeploymentNodes(node.deploymentNodes).forEach(it =>
          deploymentNodes.push(it),
        );
      }
    }
    return deploymentNodes;
  }

  function renderShape(
    pos: number,
    id: string,
    colorSchema: ColorSchema,
    title: string,
    tooltip: string,
    type: string,
    clickable: boolean,
    editable: boolean,
  ): string {
    const shape = type === 'Database' ? 'cylinder' : 'box';
    const clickableClassName = clickable ? 'clickable' : '';
    const className = edit && editable ? 'contextmenu' : clickableClassName;
    return `${pos} [shape=${shape}, id="${id}", label=<
            <table border='0' cellborder='0' cellspacing='0'>
                <tr>
                    <td><b><font point-size='10'>${wrapText(title)}</font></b></td>
                </tr>
                <tr>
                    <td><font point-size='9'>[${type}]</font></td>
                </tr>
            </table>>
        , fontcolor="${colorSchema.fontcolor}", color="${colorSchema.border}", fillcolor="${colorSchema.background}", tooltip="${tooltip ? wrapTooltip(tooltip) : 'Description not available'}", style=filled, width=2, class="${className} ${type}"]`;
  }

  function renderRelation(
    id: string,
    source: number,
    target: number,
    description?: string,
  ): string {
    return `${source} -> ${target} [id="${id}", label=<<font point-size='10'>${wrapText(description)}</font>> style=dashed, tooltip="${wrapText(description)}", color="${theme.palette.textSubtle}", fillcolor="${theme.palette.textSubtle}", fontcolor="${theme.palette.textSubtle}", class="contextmenu"]`;
  }

  function renderRelationToCluster(
    id: string,
    source: number,
    target: number,
    description?: string,
  ): string {
    return `${source} -> ${target} [id="${id}",lhead=cluster1, label=<<font point-size='10'>${wrapText(description)}</font>> style=dashed, tooltip="${wrapText(description)}", color="${theme.palette.textSubtle}", fillcolor="${theme.palette.textSubtle}", fontcolor="${theme.palette.textSubtle}"]`;
  }

  function renderRelationFromCluster(
    id: string,
    source: number,
    target: number,
    description?: string,
  ): string {
    return `${source} -> ${target} [id="${id}",ltail=cluster1, label=<<font point-size='10'>${wrapText(description)}</font>> style=dashed, tooltip="${wrapText(description)}", color="${theme.palette.textSubtle}", fillcolor="${theme.palette.textSubtle}", fontcolor="${theme.palette.textSubtle}"]`;
  }

  function renderContainer(pos: number, entity: C4DiagramEntity): string {
    const typeTitle = entity.external ? `External ${entity.type}` : entity.type;
    const colorIndex = entity.context ? 'Context' : typeTitle;
    const colorShema = isValid(entity) ? colors[colorIndex] : colors['Error'];
    return renderShape(
      pos,
      entity.id,
      colorShema,
      entity.text,
      entity.description,
      typeTitle,
      !!entity.link,
      true,
    );
  }

  function renderDeploymentNode(node: C4DiagramDeploymentNode): string {
    const content: string[] = [];
    if (node.deploymentNodes) {
      for (const subNode of node.deploymentNodes) {
        content.push(renderDeploymentNode(subNode));
      }
    }
    if (node.entities) {
      for (const entity of node.entities) {
        content.push(
          renderContainer(position.indexOf(entity.id), entity.entity),
        );
      }
    }
    const clickableClassName = node.link ? 'clickable' : '';
    const className = edit ? 'contextmenu' : clickableClassName;
    return `subgraph cluster_${node.id.replaceAll('-', '_')} {
            graph [rank=same, id="${node.id}", color="${theme.palette.textSubtle}", fontcolor="${theme.palette.textSubtle}", style=solid, label=<
            <table border='0' cellborder='0' cellspacing='0'>
                <tr>
                    <td><b><font point-size='10'>${wrapText(node.text)}</font></b></td>
                </tr>
                <tr>
                    <td><font point-size='9'>[Deployment Node]</font></td>
                </tr>
            </table>>, labelloc="b", tooltip="${wrapTooltip(node.text)}", fontname="Helvetica", class="${className}"]
            ${content.join('\n')}
        }`;
  }

  function renderContextCluster(
    context: C4DiagramEntity,
    content: string,
  ): string {
    const clickableClassName = context.link ? 'clickable' : '';
    const className = edit ? 'contextmenu' : clickableClassName;
    const color = colors['Context'];
    return `subgraph cluster1 {
            graph [rank=same, id="${context.id}", color="${color.background}", fontcolor="${color.background}", style=dashed, label="${context.text}", tooltip="${wrapTooltip(context.description)}", fontname="Helvetica", class="${className}"]
            ${position.indexOf(context.id)} [height=0, width=0, style=invis]
            ${content}
        }`;
  }

  function compare(a: C4DiagramEntity, b: C4DiagramEntity) {
    if (a.external < b.external) {
      return -1;
    } else if (a.type !== b.type) {
      return a.type.localeCompare(b.type);
    } else if (a.external === b.external && a.text && b.text) {
      return a.text.localeCompare(b.text);
    }
    return 1;
  }

  function findEntity(id: string): C4DiagramEntity | undefined {
    const allEntities = getDeploymentNodeEntities(model.deploymentNodes).map(
      it => it.entity,
    );
    if (model.context) {
      allEntities.push(model.context);
    }
    model.entities?.forEach(it => allEntities.push(it));
    model.context?.entities?.forEach(it => allEntities.push(it));
    return allEntities.find(e => e.id === id);
  }

  function findDeploymentNodeEntity(
    id: string,
  ): C4DiagramDeploymentNodeEntity | undefined {
    const allEntities = getDeploymentNodeEntities(model.deploymentNodes);
    return allEntities.find(e => e.id === id);
  }

  function findDeploymentNode(id: string): C4DiagramDeploymentNode | undefined {
    const allDeploymentNodes = getDeploymentNodes(model.deploymentNodes);
    return allDeploymentNodes.find(e => e.id === id);
  }

  function isValid(entity: C4DiagramEntity): boolean {
    if (
      (model.type === 'context' && model.context === entity) ||
      model.type === 'container'
    ) {
      const dependsOn = model.relations.filter(it => it.source === entity.id);
      const dependencyOf = model.relations.filter(
        it => it.target === entity.id,
      );
      if (
        entity.type === 'Website' ||
        (entity.type === 'System' && entity.context && !entity.external)
      ) {
        return dependencyOf.length > 0;
      } else {
        return dependencyOf.length > 0 || dependsOn.length > 0;
      }
    }
    return true;
  }

  const lines: string[] = [];
  const position: string[] = [];
  if (model.context) {
    position.push(model.context.id);
  }
  const sortedEntities = model.entities.sort((a, b) => compare(a, b));
  sortedEntities.map(it => it.id).forEach(it => position.push(it));
  getDeploymentNodeEntities(model.deploymentNodes)
    .sort((a, b) => compare(a.entity, b.entity))
    .map(it => it.id)
    .forEach(it => position.push(it));
  model.relations = model.relations.filter(
    it =>
      (findEntity(it.target) && findEntity(it.source)) ||
      (findDeploymentNodeEntity(it.target) &&
        findDeploymentNodeEntity(it.source)),
  );

  lines.push('digraph {');
  lines.push('compound=true;');
  lines.push(`bgcolor="${theme.palette.background.default}"`);
  lines.push('rankdir="RL"');
  lines.push('node [fontname="Helvetica"];');
  lines.push('edge [fontname="Helvetica"];');
  if (model.context && model.deploymentNodes) {
    const content: string[] = [];
    for (const node of model.deploymentNodes) {
      content.push(renderDeploymentNode(node));
    }
    lines.push(renderContextCluster(model.context, content.join('\n')));
  } else if (model.context?.entities) {
    const clickableClassName = model.context.link ? 'clickable' : '';
    const className = edit ? 'contextmenu' : clickableClassName;
    const color = colors['Context'];
    lines.push('subgraph cluster1 {');
    lines.push(
      `graph [rank=same, id="${model.context.id}", color="${color.background}", fontcolor="${color.background}", style=dashed, label="${model.context.text}", tooltip="${wrapTooltip(model.context.description)}", fontname="Helvetica", class="${className}"]`,
    );
    lines.push(
      `${position.indexOf(model.context.id)} [height=0, width=0, style=invis]`,
    );
    const sortedComponents = model.context.entities!.sort((a, b) =>
      compare(a, b),
    );
    sortedComponents.map(it => it.id).forEach(it => position.push(it));
    for (const entity of sortedComponents) {
      if (
        entity.type === 'System' ||
        entity.type === 'Service' ||
        entity.type === 'Database' ||
        entity.type === 'Website' ||
        entity.type === 'Job' ||
        entity.type === 'Workflow' ||
        entity.type === 'Library'
      ) {
        lines.push(renderContainer(position.indexOf(entity.id), entity));
      }
    }
    lines.push('}');
  } else if (model.context) {
    lines.push(
      renderContainer(position.indexOf(model.context.id), model.context),
    );
  }

  for (const entity of sortedEntities) {
    lines.push(renderContainer(position.indexOf(entity.id), entity));
  }

  // The order of the components depends of the order of the relations
  const sortedRelations = model.relations.sort((a, b) =>
    position.indexOf(a.target) < position.indexOf(b.target) ? -1 : 1,
  );
  for (const rel of sortedRelations) {
    if (model.context?.entities && position.indexOf(rel.source) === 0) {
      lines.push(
        renderRelationFromCluster(
          rel.id,
          0,
          position.indexOf(rel.target),
          rel.text,
        ),
      );
    } else if (model.context?.entities && position.indexOf(rel.target) === 0) {
      lines.push(
        renderRelationToCluster(
          rel.id,
          position.indexOf(rel.source),
          0,
          rel.text,
        ),
      );
    } else {
      lines.push(
        renderRelation(
          rel.id,
          position.indexOf(rel.source),
          position.indexOf(rel.target),
          rel.text,
        ),
      );
    }
  }
  lines.push('}');

  instance().then((viz: any) => {
    const graph = document.getElementById('graph');
    if (graph) {
      graph.innerHTML = '';
      const svgElement = viz.renderSVGElement(lines.join('\n'));
      const scale = zoom / 100;
      svgElement.setAttribute(
        'style',
        `transform: scale(${scale}); transform-origin: 0 0;`,
      );
      svgElement.addEventListener('click', (e: any) => {
        let path = e.composedPath ? e.composedPath() : e.path;
        if (
          path &&
          path.length > 4 &&
          path[3].className?.baseVal?.includes('clickable')
        ) {
          const id = path[3].id;
          const entity = findEntity(id);
          const deploymentNode = findDeploymentNode(id);
          if (entity?.link) {
            navigate(entity.link);
          } else if (deploymentNode?.link) {
            navigate(deploymentNode.link);
          }
        }
      });
      svgElement.addEventListener('contextmenu', (e: any) => {
        if (edit && props.openContextMenu) {
          e.preventDefault();
          let path = e.composedPath ? e.composedPath() : e.path;
          if (
            path &&
            path.length > 4 &&
            path[3].className?.baseVal?.includes('contextmenu')
          ) {
            props.openContextMenu(path[3]);
          } else if (
            path &&
            path.length > 2 &&
            path[1].className?.baseVal?.includes('contextmenu')
          ) {
            props.openContextMenu(path[1]);
          }
          return false;
        }
        return true;
      });
      graph.appendChild(svgElement);
    }
  });

  return (
    <>
      <NumberInput
        value={zoom}
        min={5}
        max={200}
        step={5}
        onChange={(_event, value) => setZoom(value as number)}
      />
      <div id="graph"></div>
    </>
  );
};
